import { Injectable, Inject } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Meraki } from '@app/shared/models/meraki/meraki.model';
import { MerakiSwitchStatus } from '@app/shared/models/meraki/meraki-switch-status.model';
import { MerakiNetwork } from '@app/shared/models/meraki/meraki-network.model';
import { MerakiAdmin } from '@shared/models/meraki/meraki-admin.model';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { timeout, retry, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class MerakisService {
    private apiUrl = `${environment.apiHost}/meraki`;

    constructor(private http: HttpClient, @Inject(OKTA_AUTH) public oktaAuth: OktaAuth) {
    }

    async findByStoreNumber(storeNumber: string) {
        return this.http.get<Meraki>(`${this.apiUrl}/switches?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Request for merakis timed out!"))
            );
    }

    async getNetwork(storeNumber: string) {
        return this.http.get<MerakiNetwork>(`${this.apiUrl}/network?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Request for merakis timed out!"))
            );
    }

    async createNetwork(name: string, type: string, tags: string) {
        return this.http.post(`${this.apiUrl}/network`,
            {
                "name": name,
                "type": type,
                "tags": tags
            },
            {
                headers: new HttpHeaders(
                    {
                        "Authorization": "Bearer " + await this.oktaAuth.getAccessToken()
                    }
                )
            }
        ).pipe(
            timeout(10000),
            retry(3),
            catchError(error => of("Request to create meraki network timed out!"))
        );
    }

    async bindNetwork(networkId: string, configTemplateId: string) {
        return this.http.put(`${this.apiUrl}/bind-network`,
            {
                "networkId": networkId,
                "configTemplateId": configTemplateId
            },
            {
                headers: new HttpHeaders(
                    {
                        "Authorization": "Bearer " + await this.oktaAuth.getAccessToken()
                    }
                )
            }
        ).pipe(
            timeout(10000),
            retry(3),
            catchError(error => of("Request to bind meraki network timed out!"))
        );
    }

    async getSwitchStatuses(storeNumber: string) {
        return this.http.get<MerakiSwitchStatus>(`${this.apiUrl}/switch-statuses?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(30000),
                retry(3),
                catchError(error => of("Request for meraki switch statuses timed out!"))
            );
    }

    async getSwitchStatusesLatent(storeNumber: string) {
        return this.http.get<MerakiSwitchStatus>(`${this.apiUrl}/switch-statuses-latent?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(30000),
                retry(3),
                catchError(error => of("Request for meraki switch statuses timed out!"))
            );
    }

    async getAllSwitches() {
        return this.http.get<MerakiSwitchStatus>(`${this.apiUrl}/all-switches-latent`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(30000),
                retry(3),
                catchError(error => of("Request for meraki switch statuses timed out!"))
            );
    }

    async updateNetworkTags(network_id: string, tags: string) {
        return this.http.put<MerakiNetwork>(`${this.apiUrl}/update-network-tags`,
            {
                "network_id": network_id,
                "tags": tags
            },
            {
                headers: new HttpHeaders(
                    {
                        "Authorization": "Bearer " + await this.oktaAuth.getAccessToken()
                    }
                )
            }
        ).pipe(
            timeout(10000),
            retry(3),
            catchError(error => of("Request for merakis timed out!"))
        );
    }

    async getMerakiAdmins() {
        return this.http.get<MerakiAdmin>(`${this.apiUrl}/admins`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }
        ).pipe(
            timeout(10000),
            retry(3),
            catchError(error => of("Request for meraki admins timed out!"))
        );
    }

    async getConfigTemplates() {
        return this.http.get(`${this.apiUrl}/config-templates`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }
        ).pipe(
            timeout(10000),
            retry(3),
            catchError(error => of("Request for config templates timed out!"))
        );
    }

    async claimSwitch(networkId: string, serial: string) {
        return this.http.put(`${this.apiUrl}/claim-switch`,
            {
                "networkId": networkId,
                "serial": serial
            },
            {
                headers: new HttpHeaders(
                    {
                        "Authorization": "Bearer " + await this.oktaAuth.getAccessToken()
                    }
                )
            }
        ).pipe(
            timeout(10000),
            retry(3),
            catchError(error => of("Request to claim meraki switch timed out!"))
        );
    }

    async getPortStatuses(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/port-statuses?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }
        ).pipe(
            timeout(15000),
            retry(3),
            catchError(error => of("Request for port statuses timed out!"))
        );
    }

    async getDevicesLatent(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/devices-latent?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }
        ).pipe(
            timeout(15000),
            retry(3),
            catchError(error => of("Request for devices latent timed out!"))
        );
    }

    async getClientsByManufacturerLimited(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/network-clients-by-manufacturer-limited?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }
        ).pipe(
            timeout(15000),
            retry(3),
            catchError(error => of("Request for clients by manufacturer timed out!"))
        ); 
    }
}